import { useMutation } from 'react-query';
import React, { useState, useEffect } from 'react';
import Modal from '_components/utils/Modal/Modal';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { userStatus } from '_constants';
import { profileService } from '_services/profile.service';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon-grey.svg';

export const useUpdateProfile = (
  refetchUser,
  refetchHistory,
  onSuccessCallback,
  setDisableSave
) => {
  return useMutation(
    ({ userId, formData }) => profileService.updateProfile(userId, formData),
    {
      onSuccess: (data, variables) => {
        refetchUser();
        const { formData } = variables; // Access formData here

        if (formData?.employment_history_id) {
          refetchHistory();
        }
        toast.success('Profile updated successfully!');
        onSuccessCallback();
        setDisableSave(false);
      },
      onError: (error) => {
        const validationErrors = error?.response?.data?.errors;
        let firstError = '';

        if (validationErrors && typeof validationErrors === 'object') {
          const firstKey = Object.keys(validationErrors)[0];
          if (
            Array.isArray(validationErrors[firstKey]) &&
            validationErrors[firstKey].length > 0
          ) {
            firstError = validationErrors[firstKey][0];
          }
        } else {
          // fallback message
          firstError =
            error?.response?.data?.message || 'An unknown error occurred.';
        }

        setDisableSave(false);
        toast.error('Failed to update profile. ' + firstError);
      },
    }
  );
};

export const EditProfile = ({
  label,
  profileData,
  refetchUser,
  refetchHistory,
  authUser,
  hasPermissionRequiredToEditOwn,
  hasPermissionToEditOthers,
  editClicked,
  children,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [lastSavedProfile, setLastSavedProfile] = useState(profileData);

  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  useEffect(() => {
    setValue('ssn', lastSavedProfile?.ssn || '');
    setValue('phone', lastSavedProfile?.phone || '');
    setValue('second_phone', lastSavedProfile?.second_phone || '');
    setValue(
      'emg_contact_1_phone',
      lastSavedProfile?.emg_contact_1_phone || ''
    );
    setValue(
      'emg_contact_2_phone',
      lastSavedProfile?.emg_contact_2_phone || ''
    );
  }, [lastSavedProfile, setValue]);

  const ownProfile = profileData?.id == authUser?.id;

  const EditForm = ({
    children,
    register,
    errors,
    setValue,
    control,
    handleSubmit,
    handleSave,
    handleCancel,
    watch,
  }) => (
    <form onSubmit={handleSubmit(handleSave)}>
      <div className="modal-content-left">
        {children(register, errors, setValue, control, watch)}
      </div>
      <div className="button-holder button-holder--right">
        <button
          type="button"
          disabled={disableSave}
          className="button grey"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button disabled={disableSave} className="button" type="submit">
          Save
        </button>
      </div>
    </form>
  );

  const mutation = useUpdateProfile(
    refetchUser,
    refetchHistory,
    () => {
      setIsModalOpen(false); // Close the modal on success
    },
    setDisableSave
  );

  // Compare current form data with original profile data and send only changed fields
  const handleSave = (formData) => {
    setDisableSave(true);
    const updatedFields = {};
    // Compare each field in the formData with the profileData
    Object.keys(formData).forEach((key) => {
      if (
        !(
          (
            formData[key] == lastSavedProfile[key] || // Values are strictly equal
            ([null, '', undefined].includes(formData[key]) &&
              [null, '', undefined].includes(lastSavedProfile[key]))
          ) // Both are null, "", or undefined
        ) ||
        (formData[key] == '' && lastSavedProfile[key] == 0) ||
        [
          'employment_history_id',
          'employment_start_date',
          'employment_end_date',
          'last_day_worked_date',
          'last_day_worked_reason',
        ].includes(key)
      ) {
        updatedFields[key] = formData[key];
      }
    });

    mutation.mutate(
      { userId: profileData.id, formData: updatedFields },
      {
        onSuccess: () => {
          setLastSavedProfile((prev) => ({ ...prev, ...updatedFields }));
        },
      }
    ); // Send only changed fields
  };

  // Function to open modal and set default form values
  const handleEdit = (fieldKey) => {
    setIsModalOpen(true);
    if (editClicked) {
      editClicked();
    }
  };

  const handleCancel = () => {
    reset(profileData);
    setIsModalOpen(false); // Close modal after saving
  };

  // 1. Active user 2. Own profile and permission not required to edit field 3. Permission to edit Others field
  const canEdit =
    profileData?.status != userStatus.Unavailable &&
    ((ownProfile &&
      hasPermissionRequiredToEditOwn &&
      profileData?.status == userStatus.Available) ||
      hasPermissionToEditOthers);

  return (
    <>
      {canEdit && (
        <>
          <button
            type="button"
            className="profile-edit-button"
            onClick={handleEdit}
          >
            edit button
            <EditIcon />
          </button>
          {isModalOpen && (
            <Modal
              isOpen={isModalOpen}
              className="update-applicant-status-modal profile-modal"
            >
              <div className="modal-head">
                <h4>Edit {label}</h4>
              </div>
              <div className="modal-body applicant-status-modal-body">
                <EditForm
                  children={children}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  handleSubmit={handleSubmit}
                  handleSave={handleSave}
                  handleCancel={handleCancel}
                  watch={watch}
                />
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  );
};
